import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate, useParams } from 'react-router-dom';

import {
  GenericResponse,
  Member,
  MemberType,
  generalLog,
} from '../../../api/ceremony';
import { downloadOutput } from '../../../api/output';
import { OrderSources, appToolOptions } from '../../../api/paywall';
import { sendWeddingApproachingReminder } from '../../../api/planner';
import { generateVowsAnalysis } from '../../../api/textGenerationAi';
import {
  getVowsAnalysis,
  VowsAnalysisResponse,
} from '../../../api/vowsAnalysis';
import {
  PaywallModal,
  UpsellGifTypes,
} from '../../../components/PaywallModal/PaywallModal';
import Button from '../../../components/v2/Button/Button';
import { DraftingModal } from '../../../components/v2/DraftingModal/DraftingModal';
import { MerchandisingModal } from '../../../components/v2/MerchandisingModal/MerchandisingModal';
import { Typography } from '../../../components/v2/Typography/Typography';
import { VowsDashboardItem } from '../../../components/v2/VowsDashboardItem/VowsDashboardItem';
import {
  getCoupleName,
  getMemberName,
  getVowsTitle,
} from '../../../helpers/ceremonyHelper';
import renderText from '../../../helpers/renderText';
import useLoader from '../../../hooks/useLoader';
import useSpeechData from '../../../hooks/useSpeechData';
import useVows from '../../../hooks/useVows';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { useDashboard } from '../../../provider/dashboardProvider';
import { useMerchandise } from '../../../provider/merchandisingProvider';
import { useAccessibleModule } from '../../../provider/moduleAccessProvider';
import { usePaywall } from '../../../provider/paywallProvider';
import { useAllQuestions } from '../../../provider/questionsProvider';
import { useSnackbar } from '../../../provider/snackbarProvider';
import { AccessControlModal } from '../../AiCeremonyDesigner/AccesControlModal';
import PlannerItem from '../PlannerDashboard/PlannerItem';

import VowBuilderLocked from './VowBuilderLocked';
import AnalyzeButton from './components/AnalyzeButton';
import MissingVowsModal from './components/MissingVowsModal';
import VowsAnalysisModal from './components/VowsAnalysisModal';

export const VowBuilderDashboard = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const {
    ceremony,
    couple1,
    couple2,
    currentUser,
    currentRole,
    setAddModalOpen,
    setMemberForEditing,
    setMemberType,
  } = useDashboard();
  const { openSnackBar } = useSnackbar();
  const { isShowing, showLoader, hideLoader } = useLoader();
  const { ritualMinutes, fetchAllQuestions } = useAllQuestions();
  const { setSource } = usePaywall();

  const {
    getVows,
    vowsCouple1,
    vowsCouple2,
    vowsCouple1Duration,
    vowsCouple2Duration,
    vowsCouple1Tone,
    vowsCouple2Tone,
    vowsCouple1TargetLength,
    vowsCouple2TargetLength,
  } = useVows();
  const {
    hasAccessToVowsAi,
    hasAccessToVowsCeremony,
    fetchAccessibleModulesIdentifiers,
    fetchingAccessibleModulesInProgress,
  } = useAccessibleModule();

  const size = useWindowSize();
  const { setAppTool } = useMerchandise();

  const { speechData, getCeremonySpeeches } = useSpeechData();

  const [openMissingVowsModal, setOpenMissingVowsModal] = useState(false);
  const [draftingModalOpen, setDraftingModalOpen] = useState(false);
  const [textGenerating, setTextGenerating] = useState(false);
  const [textGenerateComplete, setTextGenerateComplete] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationError, setGenerationError] = useState(false);
  const [openAnalysisModal, setOpenAnalysisModal] = useState(false);

  const [vowsAnalysis, setVowsAnalysis] = useState<VowsAnalysisResponse>();

  useEffect(() => {
    if (
      currentRole &&
      params.ceremonyId &&
      !isNaN(parseInt(params.ceremonyId ?? ''))
    ) {
      void fetchAllQuestions(params.ceremonyId);
      if (currentRole === MemberType.officiant) {
        void getCeremonySpeeches(parseInt(params.ceremonyId));
      }
      void fetchAccessibleModulesIdentifiers(params.ceremonyId);
      void fetchVowsAnalysis();
    }
  }, [params.ceremonyId, currentRole]);

  useEffect(() => {
    if (ceremony && couple1 && couple2)
      void getVows(ceremony.id.toString(), couple1, couple2);

    setAppTool(appToolOptions.vows_builder);
  }, [ceremony]);

  const [isPaywallModalOpen, setIsPaywallModalOpen] = useState(false);

  const onSendWeddingApproachingReminder = async (memberId: number) => {
    if (ceremony) {
      try {
        const reminderResponse: GenericResponse =
          await sendWeddingApproachingReminder(memberId, ceremony.id);
        if (reminderResponse.success) {
          openSnackBar('Reminder email sent successfully');
        }
      } catch (err) {
        openSnackBar('Something went wrong in sending reminder', 'error');
      }
    }
  };

  const fetchVowsAnalysis = async () => {
    if (!ceremony) {
      return;
    }

    try {
      const response = await getVowsAnalysis({
        ceremonyId: ceremony.id.toString(),
      });

      if (response.success) {
        setVowsAnalysis(response);
      }
    } catch (err) {
      openSnackBar('Something went wrong fetching vows analysis', 'error');
    }
  };

  if (!ceremony || !couple1 || !couple2 || !currentUser) {
    return (
      <>
        <Typography type='display-200' className='text-center'>
          Please make sure both partners are added to the ceremony before using
          the Vow Builder.
        </Typography>
      </>
    );
  }

  const onExportVowsOutputPDF = async (
    member?: Member,
    currentUser?: Member
  ) => {
    if (ceremony && member && currentUser) {
      showLoader();
      const blob = await downloadOutput(
        ceremony.id,
        'vows',
        true,
        true,
        ritualMinutes,
        member?.id
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = getVowsTitle(member, currentUser);
      document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
      a.click();
      a.remove();
      hideLoader();
    }
  };

  const onUpgradeButtonClick = () => {
    if (ceremony) {
      ReactGA.event({
        category: 'PayWall',
        action: 'Unlock',
        label: 'Vows',
      });
      setSource(OrderSources.vb_landing);
      generalLog({
        ceremony_id: ceremony.id,
        activity: `Unlock - Vows`,
      })
        .then(() => {
          setIsPaywallModalOpen(true);
        })
        .catch((err) => console.error(err));
    }
  };

  const handleAnalyzeClick = async () => {
    if (!hasAccessToVowsAi) {
      setIsPaywallModalOpen(true);
      return;
    }

    if (!hasAccessToVowsCeremony) {
      setIsPaywallModalOpen(true);
      return;
    }

    if (!couple1.email || !couple2.email) {
      openSnackBar(
        'Please invite your partner to the ceremony before analyzing your vows.',
        'error'
      );
      setMemberType(couple2.member_type);
      setMemberForEditing(couple2);
      setAddModalOpen(true);
      return;
    }

    if (!vowsCouple1 || !vowsCouple2) {
      setOpenMissingVowsModal(true);
      return;
    }

    setTextGenerating(true);
    setDraftingModalOpen(true);
    setGenerationError(false);
    setGenerationProgress(0);

    try {
      setTimeout(() => {
        setGenerationProgress(50);
      }, 4000);

      const response = await generateVowsAnalysis({
        ceremonyId: ceremony.id,
      });

      if (response.success) {
        setGenerationProgress(100);
        setTextGenerating(false);
        setTextGenerateComplete(true);
        fetchVowsAnalysis();
      }
    } catch (err) {
      setGenerationError(true);
      setTextGenerating(false);
      setTextGenerateComplete(false);
      setDraftingModalOpen(false);
      openSnackBar('Something went wrong in generating analysis', 'error');
    }
  };

  if (fetchingAccessibleModulesInProgress) {
    return <></>;
  }

  return (
    <>
      {!hasAccessToVowsAi &&
      (currentUser?.member_type === MemberType.bride ||
        currentUser?.member_type === MemberType.groom ||
        currentUser?.member_type === MemberType.other) ? (
        <VowBuilderLocked onUpgradeButtonClick={onUpgradeButtonClick} />
      ) : (
        <div className='VowBuilderDashboard VowBuilderDashboard-root flex flex-col gap-8 mt-10 px-3 md:px-0 lg:mt-0'>
          <div className='PlannerDashboard-section__header flex flex-col lg:flex-row lg:items-center max-w-full'>
            <AccessControlModal
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
            />
            <div className='flex flex-col flex-1'>
              <Typography type='display-400'>Vow Builder</Typography>
              {!size.isMobile && (
                <Typography className='max-w-[550px] mt-2' type='body-400'>
                  {renderText('vow_builder_description')}
                </Typography>
              )}
            </div>
            <div>
              {(currentUser?.member_type === MemberType.bride ||
                currentUser?.member_type === MemberType.groom ||
                currentUser?.member_type === MemberType.other) && (
                <Button
                  variant='secondary'
                  size='100'
                  onClick={() => {
                    if (hasAccessToVowsAi) {
                      setModalOpen(true);
                    } else {
                      setIsPaywallModalOpen(true);
                    }
                  }}
                >
                  Vow Privacy
                </Button>
              )}
            </div>
          </div>
          <div className='VowBuilderDashboard-vows flex flex-col gap-4'>
            {currentUser?.member_type === MemberType.officiant ? (
              <>
                <div className='flex flex-col md:flex-row gap-4 w-full'>
                  {couple1 && currentUser && (
                    <PlannerItem
                      title={getVowsTitle(couple1, currentUser)}
                      time={vowsCouple1Duration}
                      targetTime={vowsCouple1TargetLength}
                      showTime={couple1.share_vows_time_with_officiant || false}
                      progress={speechData?.vows_status_couple1 || 0}
                      allowExport={
                        couple1.share_vows_content_with_officiant || false
                      }
                      // eslint-disable-next-line @typescript-eslint/no-empty-function
                      onExportPDF={
                        couple1.share_vows_content_with_officiant
                          ? () => onExportVowsOutputPDF(couple1)
                          : undefined
                      }
                      url={`/preview-vows/${ceremony.id}/${couple1.id || ''}`}
                      //intendedTone={vowsCouple1Tone}
                      name={getMemberName(couple1)}
                      coupleName={
                        couple2
                          ? getCoupleName(couple1, couple2)
                          : getMemberName(couple1)
                      }
                      notInvited={!couple1.email}
                      pendingInvitation={!couple1.user_id}
                      isPrivate={
                        !couple1.share_vows_content_with_officiant || false
                      }
                      isDeactivated={ceremony.isCeremonyDeactivatedByPlanner}
                      onInvite={() => {
                        setMemberType(couple1.member_type);
                        setMemberForEditing(couple1);
                        setAddModalOpen(true);
                      }}
                      onSendWeddingReminder={() => {
                        couple1?.id &&
                          void onSendWeddingApproachingReminder(couple1.id);
                      }}
                    />
                  )}
                  {couple2 && currentUser && (
                    <PlannerItem
                      title={getVowsTitle(couple2, currentUser)}
                      time={vowsCouple2Duration}
                      targetTime={vowsCouple2TargetLength}
                      showTime={couple2.share_vows_time_with_officiant || false}
                      progress={speechData?.vows_status_couple2 || 0}
                      allowExport={
                        couple2.share_vows_content_with_officiant || false
                      }
                      onExportPDF={
                        couple2.share_vows_content_with_officiant
                          ? () => onExportVowsOutputPDF(couple2)
                          : undefined
                      } //onExportVowsOutputPDF
                      url={`/preview-vows/${ceremony.id}/${couple2.id || ''}`}
                      //intendedTone={vowsCouple2Tone}
                      name={getMemberName(couple2)}
                      coupleName={
                        couple1
                          ? getCoupleName(couple1, couple2)
                          : getMemberName(couple2)
                      }
                      notInvited={!couple2.email}
                      pendingInvitation={!couple2.user_id}
                      isPrivate={
                        !couple2.share_vows_content_with_officiant || false
                      }
                      isDeactivated={ceremony.isCeremonyDeactivatedByPlanner}
                      onInvite={() => {
                        setMemberType(couple2.member_type);
                        setMemberForEditing(couple2);
                        setAddModalOpen(true);
                      }}
                      onSendWeddingReminder={() => {
                        couple2?.id &&
                          void onSendWeddingApproachingReminder(couple2.id);
                      }}
                    />
                  )}
                </div>
                <div className='flex flex-row justify-center items-center'>
                  <Typography>
                    <>
                      If you want see how the vows fit into the ceremony you can
                      do so in the{' '}
                      <span
                        onClick={() =>
                          navigate(`/ceremony-designer/${ceremony.id}`)
                        }
                        className='underline cursor-pointer'
                      >
                        Script editor
                      </span>
                      .
                    </>
                  </Typography>
                </div>
              </>
            ) : (
              <>
                <VowsDashboardItem
                  variant='vows'
                  member={currentUser}
                  showVows={true}
                  showTime={true}
                  vowsTone={
                    currentUser.id === couple1.id
                      ? vowsCouple1Tone
                      : vowsCouple2Tone
                  }
                  setIsPaywallModalOpen={(isPaywallModalOpen) =>
                    setIsPaywallModalOpen(isPaywallModalOpen)
                  }
                  vows={
                    currentUser.id === couple1.id ? vowsCouple1 : vowsCouple2
                  }
                  vowsTargetLength={
                    currentUser.id === couple1.id
                      ? vowsCouple1TargetLength
                      : vowsCouple2TargetLength
                  }
                  vowsTime={
                    couple1?.id === currentUser?.id
                      ? vowsCouple1Duration
                      : vowsCouple2Duration
                  }
                  canEditVows={true}
                  hasAccess={hasAccessToVowsAi}
                  title={'Your Vows'}
                  ceremonyId={ceremony?.id.toString()}
                  text={
                    'Write meaningful vows without the stress. Answer a few curated questions about your favorite stories, memories, and moments of love, and we’ll help generate a first draft.'
                  }
                  onUpgradeButtonClick={onUpgradeButtonClick}
                />
                <AnalyzeButton
                  vowsAnalysis={vowsAnalysis}
                  handleClick={() => {
                    if (
                      hasAccessToVowsCeremony &&
                      vowsAnalysis?.data?.vows_analysis
                    ) {
                      setOpenAnalysisModal(true);
                      return;
                    }

                    handleAnalyzeClick();
                  }}
                  hasAccess={hasAccessToVowsCeremony}
                />
                <VowsDashboardItem
                  variant='vows'
                  vows={
                    currentUser.id === couple1.id ? vowsCouple2 : vowsCouple1
                  }
                  member={couple1?.id === currentUser?.id ? couple2 : couple1}
                  showTime={
                    couple1?.id === currentUser?.id
                      ? couple2?.share_vows_time_with_partner
                      : couple1?.share_vows_time_with_partner
                  }
                  showVows={
                    couple1?.id === currentUser?.id
                      ? couple2?.share_vows_content_with_partner
                      : couple1?.share_vows_content_with_partner
                  }
                  vowsTime={
                    couple1?.id === currentUser?.id
                      ? vowsCouple2Duration
                      : vowsCouple1Duration
                  }
                  vowsTone={
                    couple1?.id === currentUser?.id
                      ? vowsCouple2Tone
                      : vowsCouple1Tone
                  }
                  vowsTargetLength={
                    couple1?.id === currentUser?.id
                      ? vowsCouple2TargetLength
                      : vowsCouple1TargetLength
                  }
                  canEditVows={false}
                  title={getVowsTitle(
                    couple1?.id === currentUser?.id ? couple2 : couple1,
                    currentUser
                  )}
                  text={
                    (
                      couple1?.id === currentUser?.id
                        ? couple2?.share_vows_content_with_partner
                        : couple1?.share_vows_content_with_partner
                    )
                      ? 'View your partners’ work-in-progress vows here to collaborate on your mutual speeches ahead of the big day.'
                      : 'Your partner wants to keep their vows a surprise for the big day. No peeking allowed :), but you can see their relative length and tone here to ensure your vows are balanced.'
                  }
                  ceremonyId={ceremony.id.toString()}
                  onUpgradeButtonClick={onUpgradeButtonClick}
                />
              </>
            )}
          </div>

          {currentUser?.id ? (
            <MerchandisingModal
              ceremonyId={ceremony.id.toString()}
              ceremonyDate={ceremony.date}
              userId={currentUser.id?.toString()}
            />
          ) : null}
        </div>
      )}
      <PaywallModal
        isOpen={isPaywallModalOpen}
        onClose={() => setIsPaywallModalOpen(false)}
        type={'vows'}
        referrer={'vows'}
        ceremonyId={ceremony.id}
        displayGif={UpsellGifTypes.vowsBuilder}
      />
      <MissingVowsModal
        isOpen={openMissingVowsModal}
        onClose={() => {
          setOpenMissingVowsModal(false);
        }}
      />

      <DraftingModal
        generationError={generationError}
        modalOpen={draftingModalOpen}
        setModalOpen={setDraftingModalOpen}
        textGenerateComplete={textGenerateComplete}
        textGenerating={textGenerating}
        aiGeneratedTextIndex={generationProgress}
        titleDrafting={'Analyzing both vows...'}
        descriptionDrafting={
          'We’re using your vows to analyze the tone, length, and content of both speeches. Once complete, you’ll be able to view and adjust your vows to ensure they are balanced and personalized.'
        }
        titlePopulating={'Finishing up...'}
        descriptionPopulating={
          'Just a moment while we finish analyzing your vows. Once complete, you’ll be able to view and adjust your vows to ensure they are balanced and personalized.'
        }
        titleComplete={'Analysis complete!'}
        descriptionComplete={
          'We’ve analyzed both partner’s vows and generated a summary of findings for you to review.'
        }
        buttonTextComplete={'View Analysis'}
        buttonTextCompleteClick={() => {
          setOpenAnalysisModal(true);
        }}
      />
      <VowsAnalysisModal
        isOpen={openAnalysisModal}
        onClose={() => {
          setOpenAnalysisModal(false);
        }}
        vowsAnalysis={vowsAnalysis}
        currentUser={currentUser}
        onAnalyzeAgain={() => {
          setOpenAnalysisModal(false);
          setTextGenerating(true);
          setDraftingModalOpen(true);
          setTextGenerateComplete(false);
          setGenerationProgress(0);
          handleAnalyzeClick();
        }}
      />
    </>
  );
};
